import React, { useState, useEffect } from 'react';

import { useChatInteract } from '@chainlit/react-client';

import { Translator } from '@/components/i18n';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { BASE_URL, THREAD_CATEGORIES_ENDPOINT } from '@/lib/constants';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { authController } from '@/states/authStateController';

import { EditSquare } from '../icons/EditSquare';

type NewChatDialogProps = {
  open: boolean;
  handleClose: () => void;
  handleConfirm: (category: string) => void;
  categories: string[];
  loading: boolean;
};

export const NewChatDialog = ({
  open,
  handleClose,
  handleConfirm,
  categories,
  loading,
}: NewChatDialogProps) => {
  const [category, setCategory] = useState<string>('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    if (!open) {
      setCategory('');
      setCategoryOpen(false);
      setSearchValue('');
    }
  }, [open]);

  const canConfirm = category.trim().length > 0;

  const handleConfirmClick = () => {
    if (canConfirm) {
      handleConfirm(category);
    }
  };

  const filteredCategories = categories.filter(cat =>
    cat.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleCommandInputChange = (value: string) => {
    setSearchValue(value);
    if (value.trim()) {
      setCategory(value.trim());
    }
  };

  const handleSelectCategory = (selectedCategory: string) => {
    setCategory(selectedCategory);
    setSearchValue(selectedCategory);
    setCategoryOpen(false);
  };

  const handleCommandKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only handle Enter when creating a new category (no filtered results)
    // Otherwise, let cmdk handle the default selection behavior
    if (e.key === 'Enter' && searchValue.trim() && filteredCategories.length === 0) {
      e.preventDefault();
      const newCategory = searchValue.trim();
      setCategory(newCategory);
      setCategoryOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent id="new-chat-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Translator path="navigation.newChat.dialog.title" />
          </DialogTitle>
          <DialogDescription>
            <Translator path="navigation.newChat.dialog.description" />
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 max-w-full min-w-0">
          <Label htmlFor="category" className="mb-2 block">
            Category <span className="text-destructive">*</span>
          </Label>
          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={categoryOpen}
                className="w-full justify-between min-h-10"
                disabled={loading}
              >
                <span className="truncate text-left flex-1">
                  {category || 'Select or type category...'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0 overflow-hidden"
              align="start"
            >
              <Command shouldFilter={false} className="max-w-full min-w-0 w-full">
                <CommandInput
                  placeholder="Search or create category..."
                  value={searchValue}
                  onValueChange={handleCommandInputChange}
                  onKeyDown={handleCommandKeyDown}
                />
                <CommandList>
                  {filteredCategories.length === 0 && searchValue.trim() ? (
                    <CommandEmpty className="max-w-full min-w-0">
                      <div className="py-2 px-2 text-center text-sm break-all overflow-hidden max-w-full min-w-0 w-full">
                        Press Enter to create "{searchValue.trim()}"
                      </div>
                    </CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {filteredCategories.map(cat => (
                        <CommandItem
                          key={cat}
                          value={cat}
                          onSelect={() => handleSelectCategory(cat)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0',
                              category === cat ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <span className="truncate">{cat}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {category && !categories.includes(category) && (
            <p className="mt-2 text-sm text-muted-foreground break-all overflow-hidden max-w-full min-w-0">
              New category will be created: "{category}"
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            <Translator path="common.actions.cancel" />
          </Button>
          <Button
            variant="default"
            onClick={handleConfirmClick}
            id="confirm"
            disabled={!canConfirm}
          >
            <Translator path="common.actions.confirm" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  navigate?: (to: string) => void;
}

const NewChatButton = ({ navigate, ...buttonProps }: Props) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { clear } = useChatInteract();
  const { user } = authController.useState(['user']);

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    if (!user?.id) {
      console.error('User not found, cannot fetch categories');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}${THREAD_CATEGORIES_ENDPOINT}?user_id=${user.id}`
      );
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = (category: string) => {
    // Store category in localStorage to be associated when thread is created
    localStorage.setItem('pendingThreadCategory', category);
    clear();
    navigate?.('/');
    handleClose();
  };

  return (
    <div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              id="new-chat-button"
              className="text-muted-foreground hover:text-muted-foreground"
              onClick={handleClickOpen}
              {...buttonProps}
            >
              <EditSquare className="!size-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <Translator path="navigation.newChat.dialog.tooltip" />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <NewChatDialog
        open={open}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        categories={categories}
        loading={loading}
      />
    </div>
  );
};

export default NewChatButton;
